import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './services/authContext';
import { ToastProvider } from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';

// Public pages
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// Shared pages
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import Calendar from './pages/Calendar';
import Settings from './pages/Settings';

// Admin pages
import UserManagement from './pages/UserManagement';
import Students from './pages/Students';
import Teachers from './pages/Teachers';
import Classes from './pages/Classes';
import Subjects from './pages/Subjects';
import Programmes from './pages/Programmes';
import Attendance from './pages/Attendance';
import Reports from './pages/Reports';
import Announcements from './pages/Announcements';
import Notifications from './pages/Notifications';
import Materials from './pages/Materials';
import AssignmentTasks from './pages/AssignmentTasks';
import Timetable from './pages/Timetable';

// Lecturer pages
import MyAttendance from './pages/MyAttendance';
import Grades from './pages/Grades';
import Exams from './pages/Exams';

// Student pages
import MyClasses from './pages/MyClasses';
import MySubjects from './pages/MySubjects';
import MyExams from './pages/MyExams';
import MyGrades from './pages/MyGrades';
import MyMaterials from './pages/MyMaterials';
import MyAssignments from './pages/MyAssignments';

// Parent pages
import MyChildren from './pages/MyChildren';
import ChildrenAttendance from './pages/ChildrenAttendance';
import ChildrenGrades from './pages/ChildrenGrades';
import ChildrenAssignments from './pages/ChildrenAssignments';

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div className="app-container">
      <Navbar toggleSidebar={() => setSidebarOpen(o => !o)} />
      <div className="main-container">
        <Sidebar isOpen={sidebarOpen} closeSidebar={() => setSidebarOpen(false)} />
        <div className="content">{children}</div>
      </div>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

const Protected = ({ children, roles = [] }) => (
  <ProtectedRoute allowedRoles={roles}>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

const App = () => (
  <AuthProvider>
    <ToastProvider>
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login"            element={<Login />} />
        <Route path="/unauthorized"      element={<Unauthorized />} />
        <Route path="/forgot-password"   element={<ForgotPassword />} />
        <Route path="/reset-password"    element={<ResetPassword />} />

        {/* Shared */}
        <Route path="/"          element={<Protected><Dashboard /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/messages"  element={<Protected><Messages /></Protected>} />
        <Route path="/calendar"  element={<Protected><Calendar /></Protected>} />
        <Route path="/settings"  element={<Protected><Settings /></Protected>} />

        {/* Admin */}
        <Route path="/users"         element={<Protected roles={['admin']}><UserManagement /></Protected>} />
        <Route path="/students"      element={<Protected roles={['admin','teacher']}><Students /></Protected>} />
        <Route path="/lecturers"     element={<Protected roles={['admin']}><Teachers /></Protected>} />
        <Route path="/programmes"    element={<Protected roles={['admin']}><Programmes /></Protected>} />
        <Route path="/classes"        element={<Protected roles={['admin','teacher']}><Classes /></Protected>} />
        <Route path="/modules"       element={<Protected roles={['admin','teacher']}><Subjects /></Protected>} />
        <Route path="/attendance"    element={<Protected roles={['admin','teacher']}><Attendance /></Protected>} />
        <Route path="/grades"        element={<Protected roles={['admin','teacher']}><Grades /></Protected>} />
        <Route path="/exams"         element={<Protected roles={['admin','teacher']}><Exams /></Protected>} />
        <Route path="/reports"       element={<Protected roles={['admin']}><Reports /></Protected>} />
        <Route path="/announcements" element={<Protected><Announcements /></Protected>} />
        <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
        <Route path="/materials"     element={<Protected roles={['admin','teacher']}><Materials /></Protected>} />
        <Route path="/assignments"   element={<Protected roles={['admin','teacher']}><AssignmentTasks /></Protected>} />
        <Route path="/timetable"     element={<Protected roles={['admin','teacher']}><Timetable /></Protected>} />

        {/* Student */}
        <Route path="/my-programme"   element={<Protected roles={['student']}><MyClasses /></Protected>} />
        <Route path="/my-modules"      element={<Protected roles={['student']}><MySubjects /></Protected>} />
        <Route path="/my-attendance"   element={<Protected roles={['student']}><MyAttendance /></Protected>} />
        <Route path="/my-exams"        element={<Protected roles={['student']}><MyExams /></Protected>} />
        <Route path="/my-grades"       element={<Protected roles={['student']}><MyGrades /></Protected>} />
        <Route path="/my-materials"    element={<Protected roles={['student']}><MyMaterials /></Protected>} />
        <Route path="/my-assignments"  element={<Protected roles={['student']}><MyAssignments /></Protected>} />

        {/* Parent */}
        <Route path="/my-children"          element={<Protected roles={['parent']}><MyChildren /></Protected>} />
        <Route path="/children-attendance"  element={<Protected roles={['parent']}><ChildrenAttendance /></Protected>} />
        <Route path="/children-grades"      element={<Protected roles={['parent']}><ChildrenGrades /></Protected>} />
        <Route path="/children-assignments" element={<Protected roles={['parent']}><ChildrenAssignments /></Protected>} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
    </ToastProvider>
  </AuthProvider>
);

export default App;
